<script setup lang="ts" generic="T, C extends ColumnModel, R extends RecordMap<C>, F extends Record<string, unknown>">
import { PV } from "@/widgets";
import * as devalue from "devalue";
import { isEqual, isNil, isNotNil, noop, omitBy } from "es-toolkit";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useProfileStore } from "../../stores/profile.store";
import type { ColumnModel, defineTableSource, RecordMap, TableColumn } from "../common";
import type { FilterController } from "../filter";
import { FilterableTable, FilterApplyButtons, FilterToggleButton } from "../internal";

const props = withDefaults(
    defineProps<{
        table: ReturnType<typeof defineTableSource<T, C, R, F>>;
        expandable?: boolean;
        controller: FilterController<F>;
    }>(),
    {
        expandable: true,
    },
);

const emits = defineEmits<{
    update: [data: R[]];
}>();

const slots = defineSlots<
    TableColumn<R> & {
        _tools: () => any;
        _filter: (data: { expanded: boolean }) => any;
        _buttons: () => any;
    }
>();

const route = useRoute();
const router = useRouter();
const profile = useProfileStore().data;

function qnum(name: string, defaultValue: number) {
    if (route.query[name] === undefined) {
        return defaultValue;
    }

    try {
        return parseInt(route.query[name] as string);
    } catch (_) {
        return defaultValue;
    }
}

const isExpanded = ref(qnum("expanded", 0) === 1);
const page = ref<number>(qnum("page", 0));
const rows = ref<number>(qnum("rows", 10));
const search = ref<string | undefined>(route.query["search"] as string | undefined);

const tableCols = computed(() => {
    const kind = profile?.kind;

    let keys: Set<keyof R>;
    if (isNil(kind)) {
        keys = new Set([]);
    } else {
        keys = new Set(props.table.visible[kind] ?? []);
    }

    return props.table.columns.filter((c) => keys.has(c.field));
});

function castName(name: string) {
    return name as keyof typeof slots;
}

function reload() {
    let filter: string | undefined;
    const compact = omitBy(props.controller.active.value, isNil);
    if (Object.keys(compact).length > 0) {
        filter = btoa(devalue.stringify(props.controller.active.value));
    }

    const emptyUndefined = (str: string | undefined) => {
        if (str === undefined) return undefined;
        if (str.length === 0) return undefined;
        return str;
    };

    const newQuery = {
        page: page.value.toString(),
        rows: rows.value.toString(),
        search: emptyUndefined(search.value),
        filter: filter,
        expanded: isExpanded.value ? "1" : undefined,
    };

    if (!isEqual(route.query, omitBy(newQuery, isNil))) {
        router.replace({ path: route.path, query: newQuery });
    }
}

const applyFilter = ref(noop);
const filterPopup = ref<{ toggle: (event: Event) => void }>();

function attachApplyFn(fn: () => Promise<void>) {
    props.controller.attachRefresh(fn);
    applyFilter.value = fn;
}

function toggleFilterPopup(event: Event) {
    filterPopup.value?.toggle(event);
}

watch([page, rows, isExpanded], reload);
</script>

<template>
    <template v-if="!isExpanded">
        <FilterableTable
            v-model:page="page"
            v-model:rows="rows"
            v-model:search="search"
            :attachRefresh="attachApplyFn"
            :columns="tableCols"
            :filters="props.controller.active.value"
            :table="props.table"
            @search="reload"
            @update="(data) => emits('update', data)"
        >
            <template #headers="{ refresh }">
                <div class="flex flex-col gap-2">
                    <div class="flex flex-col md:flex-row gap-2 md:gap-0 items-center justify-center py-1">
                        <div class="w-auto md:w-3/12 flex flex-row justify-start">
                            <PV.Button
                                v-if="props.expandable"
                                icon="pi pi-window-maximize"
                                severity="secondary"
                                size="small"
                                text
                                @click="() => (isExpanded = true)"
                            />

                            <PV.Button icon="pi pi-refresh" severity="secondary" size="small" text @click="refresh" />

                            <FilterToggleButton
                                v-if="isNotNil(slots._filter)"
                                :controller="props.controller"
                                @toggleFilterPopup="toggleFilterPopup"
                            />

                            <slot name="_tools"></slot>
                        </div>

                        <div class="grow w-full md:w-auto">
                            <PV.IconField class="grow">
                                <PV.InputIcon class="pi pi-search" />
                                <PV.InputText v-model.trim="search" fluid placeholder="Cari" size="small" />
                                <PV.InputIcon
                                    v-if="search !== undefined && search.trim().length > 0"
                                    class="pi pi-times hover:cursor-pointer"
                                    @click="() => (search = undefined)"
                                />
                            </PV.IconField>
                        </div>

                        <div class="w-auto xl:w-3/12 flex flex-row justify-end gap-1">
                            <slot name="_buttons"></slot>
                        </div>
                    </div>

                    <PV.Popover v-if="isNotNil(slots._filter)" ref="filterPopup">
                        <div class="flex flex-col gap-3">
                            <slot :expanded="false" name="_filter"></slot>
                            <FilterApplyButtons
                                :controller="props.controller"
                                @applyFilter="props.controller.apply"
                                @resetFilter="props.controller.reset"
                            />
                        </div>
                    </PV.Popover>
                </div>
            </template>

            <template v-for="column in tableCols">
                <PV.Column class="whitespace-nowrap" :field="column.field" :header="column.title">
                    <template v-if="slots[castName(column.field)] !== undefined" #body="{ data }">
                        <slot :data="data" :name="castName(column.field)" />
                    </template>
                </PV.Column>
            </template>
        </FilterableTable>
    </template>
    <template v-else>
        <PV.Drawer v-model:visible="isExpanded" position="full">
            <template #header>
                <div class="inline-flex flex-col gap-3 w-full">
                    <div class="inline-flex flex-row gap-2">
                        <PV.IconField class="w-1/3">
                            <PV.InputIcon class="pi pi-search" />
                            <PV.InputText v-model.trim="search" fluid placeholder="Cari" size="small" />
                            <PV.InputIcon
                                v-if="search !== undefined && search.trim().length > 0"
                                class="pi pi-times hover:cursor-pointer"
                                @click="() => (search = undefined)"
                            />
                        </PV.IconField>

                        <FilterToggleButton
                            v-if="isNotNil(slots._filter)"
                            :controller="props.controller"
                            @toggleFilterPopup="toggleFilterPopup"
                        />

                        <slot name="_tools"></slot>
                    </div>

                    <PV.Popover v-if="isNotNil(slots._filter)" ref="filterPopup">
                        <div class="flex flex-col gap-3">
                            <slot :expanded="true" name="_filter"></slot>
                            <FilterApplyButtons
                                :controller="props.controller"
                                @applyFilter="props.controller.apply"
                                @resetFilter="props.controller.reset"
                            />
                        </div>
                    </PV.Popover>
                </div>
            </template>

            <FilterableTable
                v-model:page="page"
                v-model:rows="rows"
                v-model:search="search"
                :attachRefresh="attachApplyFn"
                :columns="tableCols"
                :filters="props.controller.active.value"
                :table="props.table"
                @search="reload"
            >
                <template v-for="column in tableCols">
                    <PV.Column class="whitespace-nowrap" :field="column.field" :header="column.title">
                        <template v-if="slots[castName(column.field)] !== undefined" #body="{ data }">
                            <slot :data="data" :name="castName(column.field)" />
                        </template>
                    </PV.Column>
                </template>
            </FilterableTable>
        </PV.Drawer>
    </template>
</template>
